import type { ReactNode } from 'react';
import './CartaoIndicador.css';

export type TomDoIndicador = 'neutro' | 'atencao' | 'erro';

/**
 * Um número grande do topo da visão geral, com a quebra factual dele embaixo.
 *
 * O tipo mora aqui, e não em `dados-exemplo`: a pasta de fixtures foi apagada
 * junto com os números inventados que ela alimentava.
 */
export type Indicador = {
  readonly rotulo: string;
  readonly valor: string;
  /** Complemento em corpo menor, colado ao valor (ex.: "/ 12"). */
  readonly sufixo?: string;
  readonly tom: TomDoIndicador;
  /**
   * Quebra do próprio número (as três séries, o que está retido), e nunca
   * comparação com ontem: `Operacao.registradoEm` é o relógio do APARELHO, e
   * uma operação de ontem que chega hoje mudaria o número de ontem
   * retroativamente — um indicador que anda sozinho para trás é pior do que
   * indicador nenhum. A comparação volta se o dia passado virar fechamento
   * gravado, com data de apuração própria.
   */
  readonly nota: string;
  readonly tomNota: TomDoIndicador;
};

type Props = {
  readonly indicador: Indicador;
  /** Ícone do assunto, na caixa do canto superior. Decorativo: o rótulo diz o que é. */
  readonly icone?: ReactNode;
};

/**
 * Cartão de indicador: ícone do assunto, rótulo, número grande e um rodapé
 * com a quebra do número — o rodapé é uma faixa própria, para a nota se ler
 * como fato complementar e não como parte do valor.
 */
export function CartaoIndicador({ indicador, icone }: Props) {
  const { rotulo, valor, sufixo, tom, nota, tomNota } = indicador;

  return (
    <div className="indicador">
      <div className="indicador__corpo">
        {icone !== undefined && (
          <span className="indicador__icone" aria-hidden="true">
            {icone}
          </span>
        )}
        <span className="indicador__rotulo">{rotulo}</span>
        <span className={`indicador__valor indicador__valor--${tom}`}>
          {valor}
          {sufixo !== undefined && <span className="indicador__sufixo">{sufixo}</span>}
        </span>
      </div>
      <div className={`indicador__rodape indicador__rodape--${tomNota}`}>
        <span className="indicador__ponto" />
        <span className="indicador__nota">{nota}</span>
      </div>
    </div>
  );
}
